import Axios from 'axios'
import Vue from 'vue'
import VueBus from 'vue-bus'
import addInterceptors from './interceptors'
import development from './development'

if (!Vue.bus) {
  Vue.use(VueBus)
}

Vue.bus.on('unauthorised-api-access', function () {
  localStorage.removeItem('levima_api_key')
  localStorage.removeItem('aio_current_user_id')
})

const aioAPI = Axios.create({
  baseURL: process.env.VUE_APP_AIO_API,
  timeout: 60000
})
aioAPI.defaults.headers.common['levima_api_key'] = localStorage.levima_api_key
addInterceptors(aioAPI)

const chalice = Axios.create({
  baseURL: process.env.VUE_APP_AIO_CHALICE,
  timeout: 60000,
})
chalice.defaults.headers.common['levima_api_key'] = localStorage.levima_api_key
addInterceptors(chalice)

async function _handleSuccessfulAuthResponse(response) {
  localStorage.levima_api_key = response.token;
  localStorage.aio_current_user_id = response.id;
  aioAPI.defaults.headers.common['levima_api_key'] = localStorage.levima_api_key
  chalice.defaults.headers.common['levima_api_key'] = localStorage.levima_api_key
  return response
}

function _createAuthApiWithDomain(domain) {
  return {
    create: {
      async byPassword(email, password) {
        let response = await chalice.post(`/sessions/${domain}/password`, {email, password})
        if (response.hasOwnProperty('two-factor-required')) {
          return false
        }
        return _handleSuccessfulAuthResponse(response)
      },
      async byOTP(email, password, otp) {
        let response = await chalice.post(`/sessions/${domain}/otp`, {email, password, code: otp})
        return _handleSuccessfulAuthResponse(response)
      },
      async byDeviceToken(deviceToken) {
        let response = await chalice.post(`/sessions/${domain}/device`, {deviceToken})
        return _handleSuccessfulAuthResponse(response)
      },
    },
    otp: {
      async resend(email, password) {
        await chalice.post(`/sessions/${domain}/resend_otp`, {email, password})
      }
    }
  }
}

export default {
  admin: {
    deactivateUser (id) { return aioAPI.post(`adminPortal/users/${id}/deactivate`) },

    archiveTransaction (id) { return chalice.delete(`admin/transaction/${id}`) },

    deleteCommission (commission) { return aioAPI.delete('adminPortal/commission', { data: commission }) },

    createCommission (commission) { return aioAPI.post('adminPortal/commission', commission) },

    createCommissionReceiver (userData) { return aioAPI.post('adminPortal/commissionReceiver', userData) },

    createUser (userData) { return aioAPI.post('adminPortal/users', userData) },

    getAllCompanies () { return chalice.get('admin/companies') },

    getAllCompanyRegistrations () { return chalice.get('admin/companyRegistrations') },

    getAllUsers () { return aioAPI.get('adminPortal/users') },

    getCompanyDetails (id) { return aioAPI.get(`adminPortal/companyRegistrations/${id}`) },

    getCompanyTransactions (id) { return chalice.get(`admin/company/${id}/transactions`) },

    getCommissionReceivers (month, year) { return aioAPI.get(`adminPortal/commissionReceivers?year=${year}&month=${month}`) },

    getCommissionReceiver (id) { return aioAPI.get(`adminPortal/commissionReceivers/${id}`) },

    getCommissionsForReceiver (id) { return aioAPI.get(`adminPortal/commissionsForReceiver/${id}`) },

    getReports () { return aioAPI.get('adminPortal/deviceReport') },

    getRoles () { return aioAPI.get('adminPortal/roles') },

    getUserDetails (id) { return aioAPI.get(`adminPortal/users/${id}`) },

    getUserTransactions (id) { return chalice.get(`admin/user/${id}/transactions`) },

    reactivateUser (id) { return aioAPI.post(`adminPortal/users/${id}/reactivate`) },

    setTwoFactor (id, settings) { return chalice.post(`admin/user/${id}/set_two_factor`, settings) },

    twoFactorStatus (id) { return chalice.get(`admin/user/${id}/two_factor_status`) },

    updateCommissionReceiver (id, update) { return aioAPI.put(`adminPortal/commissionReceivers/${id}`, update) },

    updateCompanyRegistration (registration) { return aioAPI.put(`adminPortal/companyRegistrations/${registration.id}`, registration) },

    updateUser (id, userData) { return aioAPI.put(`adminPortal/users/${id}`, userData) },

    usageReport (month, year) { return aioAPI.get('adminPortal/customerReport', { params: { month, year }}) }
  },

/*
  TODO: Maybe move this around into sub-objects to ease navigation now that we have a lot of methods
  Will start writing new methods in this style.
  Conventions:
    - Objects singular ('tag' rather than 'tags')
    - Plain verbs within objects ('tag.create' rather than 'createTag' or 'tag.createTag' or 'tag.new')
    - Sometimes more sensible to group by "related concern" rather than object (e.g. Xero-related actions which are
      better seen as just verbs rather than methods on an object, or administration actions in the 'admin' group).
      Make it look RESTful here when it looks RESTful on the server.
*/

  tag: {
    list() {
      return chalice.get('tags?include_pseudo')
    },
    create(name) {
      return chalice.post('tags', {name})
    },
    get(id) {
      return chalice.get(`tags/${id}`)
    },
    promote(id) {
      return chalice.put(`tags/${id}/promote`)
    },
    archive(id) {
      return chalice.put(`tags/${id}/archive`)
    },
  },

  report: {
    budget: {
      get(id, filter) {
        const params = new URLSearchParams()
        const fields = ['fromMonth', 'fromYear', 'toMonth', 'toYear']
        fields.forEach(field => {
          if (field in filter) {
            params.append(field, filter[field])
          }
        })
        return chalice.get(`reports/budget/${id}/csv?${params}`)
      },
    },
    expense: {
      get(filter) {
        const params = new URLSearchParams()
        const fields = ['fromMonth', 'fromYear', 'toMonth', 'toYear']
        fields.forEach(field => {
          if (field in filter) {
            params.append(field, filter[field])
          }
        })
        filter.tags.forEach(tag => params.append('tags', tag.name))
        return chalice.get(`reports/expenses/csv?${params}`)
      },
    },
  },

  xero: {
    connect() { return chalice.get('/xero/connect') },
    check() { return chalice.get('/xero/check') },
    disconnect() { return chalice.delete('/xero/disconnect') },
    sync() { return chalice.post('/xero/sync', {}, {noshow: true}) },
  },

  request: {
    setTags(id, tags) {  // TODO: should probably be request.tags.set for consistency?
      return chalice.put(`request/${id}/tags`, tags)
    },
    get(id) {
      return chalice.get(`request/${id}`)
    },
    create(request) {
      return chalice.post('request', request)
    },
  },

  company: {
    getId() {  // TODO: company.id.get? might be taking it too far?
      return chalice.get('company')
    },
  },

  device: {
    register: (device) => chalice.post('device', device),
  },

  adminSession: _createAuthApiWithDomain('adminPortal'),
  commissionSession: _createAuthApiWithDomain('commission'),
  session: _createAuthApiWithDomain('user'),

  development: development(aioAPI, chalice),

  approveRequest (userId, requestId, note) { return aioAPI.put(`users/${userId}/budgetRequests/${requestId}`, {id: requestId, status: 'approved', acceptanceNote: note}) },

  cancelRequest (requestId) { return aioAPI.delete(`budgetRequests/${requestId}`) },

  checkUserIdForResetPassword (uniqueId) { return aioAPI.get(`registration/checkUserId?u=${uniqueId}`) },

  checkUserIdForUserRegistration (uniqueId) { return aioAPI.get(`registration/checkUserId/${uniqueId}`) },

  checkXeroConnection() { return aioAPI.get('reports/xero/connection/check') },

  connectToXero() { return aioAPI.get('reports/xero/connect') },

  disconnectFromXero() { return aioAPI.get('reports/xero/disconnect') },

  exportToXero() { return aioAPI.get('reports/xero') },

  cancelInvite (userRegId) { return aioAPI.delete(`registration/cancelInvite/${userRegId}`) },

  createCategory (category) { return aioAPI.post('expenses/category', category) },

  createCompanyRegistration (registration) { return aioAPI.post('registration/company', registration) },

  createReferral (referral) { return aioAPI.post('referrals', referral) },

  declineRequest (userId, requestId, note) { return aioAPI.put(`users/${userId}/budgetRequests/${requestId}`, {id: requestId, status: 'declined', denialReason: note}) },

  deleteBudget (budgetId) { return aioAPI.delete(`pots/${budgetId}`) },

  deleteReceipt (receiptId) { return aioAPI.delete(`receipts/${receiptId}`) },

  freezeCard (userId) { return aioAPI.put(`users/${userId}/card/freeze`) },

  getBetaEnabled (companyId) { return chalice.get(`company/${companyId}/beta`)},

  getBilling () { return aioAPI.get('myCompany/billing') },

  getBudget (budgetId) { return aioAPI.get(`pots/${budgetId}`) },

  getBudgetReport (budgetId, fromMonth, fromYear, toMonth, toYear) { return aioAPI.get(`reports/budget/${budgetId}?fromMonth=${fromMonth}&fromYear=${fromYear}&toMonth=${toMonth}&toYear=${toYear}`) },

  getBudgets () { return aioAPI.get('pots') },

  getCard (userId) { return aioAPI.get(`users/${userId}/card`) },

  getCategories () { return aioAPI.get('expenses/categories') },

  getCommission (month, year) { return aioAPI.get(`commission?year=${year}&month=${month}`) },

  getCommissionPerCompany () { return aioAPI.get('commission/company') },

  getCommissionPerCompanyPerMonth (monthStart) { return aioAPI.get(`commission/company/month/${monthStart}`) },

  getCompanyRegistration (regToken) { return aioAPI.get(`registration/company/${regToken}`) },

  getDevice (deviceToken) { return aioAPI.get(`devices?token=${encodeURIComponent(deviceToken)}`) },

  getExpense (expenseId) { return aioAPI.get(`expenses/${expenseId}`) },

  getExpenseReport (fromMonth, fromYear, toMonth, toYear) { return aioAPI.get(`reports/expenseReport?fromMonth=${fromMonth}&fromYear=${fromYear}&toMonth=${toMonth}&toYear=${toYear}`) },

  getExpenses (userId) { return aioAPI.get(`users/${userId}/expenses`) },

  getMyCard (userId) { return aioAPI.get(`users/${userId}/myCard`) },

  getMyCompany () { return aioAPI.get('myCompany') },

  getMyTeam (managerId) { return aioAPI.get(`managers/${managerId}/myTeam`) },

  getPendingRequests (managerId) { return aioAPI.get(`managers/${managerId}/budgetRequests?listType=pending`) },

  getProcessedRequests (managerId) { return aioAPI.get(`managers/${managerId}/budgetRequests?listType=processed`) },

  getReceipt (receiptId) { return aioAPI.get(`receipts/${receiptId}`) },

  getReceipts (userId) { return aioAPI.get(`users/${userId}/receipts`) },

  getReferrals () { return aioAPI.get('referrals') },

  getRequest (requestId) { return aioAPI.get(`budgetRequests/${requestId}`) },

  getRequestActivity (requestId) { return chalice.get(`request/${requestId}/activity`, {noshow: true}) },

  getRequests (userId) { return aioAPI.get(`users/${userId}/budgetRequests`) },

  getUser (userId) { return aioAPI.get(`users/${userId}`) },

  getUserInvitations () { return aioAPI.get('users/invitations') },

  postBetaEnabled (companyId, enabled) { return chalice.post(`company/${companyId}/beta`, {enabled})},

  postBudget (budget) { return aioAPI.post('pots', budget) },

  postBudgetRequest (userId, request) { return aioAPI.post(`users/${userId}/budgetRequests`, request) },

  postBudgetTransfer (originBudgetId, targetBudgetId, amount) { return aioAPI.post('pots/transfer', {origin: originBudgetId, target: targetBudgetId, amount: amount}) },

  postCardRefund (userId, amount) { return aioAPI.post(`users/${userId}/card/refund`, {amount: amount}) },

  postCardTopUp (userId, settings) { return aioAPI.post(`users/${userId}/card/topUp`, settings) },

  postDeviceRegistration (device) { return aioAPI.post('devices/registration', device) },

  postEnquiry (enquiry) { return aioAPI.post('registration/enquiry', enquiry) },

  postInitResetPassword (email) { return aioAPI.post('registration/initResetPassword', {email: email}) },

  postMyCardRefund (userId, amount, budgetId, requestId) { return aioAPI.post(`users/${userId}/myCard/refund`, {amount: amount, potId: budgetId, requestId: requestId}) },

  postReceipt (userId, receipt) { return aioAPI.post(`users/${userId}/receipts`, receipt) },

  postResetPassword (newPassword, uniqueId) { return aioAPI.post('registration/resetPassword', {newPassword: newPassword, uniqueId: uniqueId}) },

  postRequestComment (requestId, body) {return chalice.post(`request/${requestId}/comment`, {body})},

  postUserInvitations (emails) { return aioAPI.post('registration/inviteUsers', {emails: emails}) },

  postUserRegistration (user) { return aioAPI.post('registration/user', user) },

  resendInvite (userRegId) { return aioAPI.post(`registration/resendInvite/${userRegId}`) },

  unfreezeCard (userId) { return aioAPI.put(`users/${userId}/card/unfreeze`) },

  updateBilling (billing) { return aioAPI.put('myCompany/billing', billing) },

  updateBudget (budgetId, budget) { return aioAPI.put(`pots/${budgetId}`, budget) },

  updateCategories (categories) { return aioAPI.put('expenses/categories', categories) },

  updateCategory (category) { return aioAPI.put('expenses/category', category) },

  updateCompanyRegistration (registration, regToken) { return aioAPI.put(`registration/company/${regToken}`, registration) },

  updateTransaction (transactionId, transaction) { return aioAPI.put(`expenses/${transactionId}`, transaction) },
}
